import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  CurrencyDollarIcon,
  ArrowTrendingUpIcon,
  BanknotesIcon,
  ChartBarIcon,
  TagIcon,
  CreditCardIcon
} from '@heroicons/react/24/outline';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, BarElement, CategoryScale, LinearScale, 
  PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js';

// Registrar os componentes necessários do Chart.js
ChartJS.register(
  ArcElement, 
  BarElement,
  CategoryScale, 
  LinearScale, 
  PointElement, 
  LineElement, 
  Title, 
  Tooltip, 
  Legend
);

const FinancialSection = ({ metrics, isMobile }) => {
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  const [showDetails, setShowDetails] = useState(null);
  
  // Dados simulados para diferentes análises financeiras
  const revenueData = {
    month: {
      labels: ['1', '5', '10', '15', '20', '25', '30'],
      datasets: [
        {
          label: 'Receita',
          data: [320, 420, 380, 580, 450, 620, 520],
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          tension: 0.4,
          fill: true
        }
      ]
    },
    quarter: {
      labels: ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set'],
      datasets: [
        {
          label: 'Receita',
          data: [2800, 3200, 2900, 3600, 3200, 3800, 3400, 4100, 3900],
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          tension: 0.4,
          fill: true
        }
      ]
    },
    year: {
      labels: ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'],
      datasets: [
        {
          label: 'Receita',
          data: [3000, 3200, 3800, 3600, 4200, 4500, 4300, 4800, 5100, 5400, 5200, 5600],
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          tension: 0.4,
          fill: true
        }
      ]
    }
  };
  
  // Dados para gráfico de ticket médio
  const avgTicketData = {
    labels: ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun'],
    datasets: [
      {
        label: 'Ticket Médio',
        data: [120, 125, 130, 140, 145, 150],
        backgroundColor: 'rgba(139, 92, 246, 0.7)',
        borderColor: 'rgb(139, 92, 246)',
        borderWidth: 1,
        borderRadius: 4,
      }
    ]
  };
  
  // Dados para gráfico de serviços por faturamento
  const serviceRevenueData = {
    labels: ['Corte', 'Coloração', 'Tratamento', 'Manicure', 'Pedicure', 'Outros'],
    datasets: [
      {
        data: [35, 25, 15, 10, 10, 5],
        backgroundColor: [
          'rgba(59, 130, 246, 0.7)',  // azul
          'rgba(139, 92, 246, 0.7)',  // roxo
          'rgba(236, 72, 153, 0.7)',  // rosa
          'rgba(16, 185, 129, 0.7)',  // verde
          'rgba(245, 158, 11, 0.7)',  // amarelo
          'rgba(107, 114, 128, 0.7)'  // cinza
        ],
        borderColor: [
          'rgb(59, 130, 246)',
          'rgb(139, 92, 246)',
          'rgb(236, 72, 153)',
          'rgb(16, 185, 129)',
          'rgb(245, 158, 11)',
          'rgb(107, 114, 128)'
        ],
        borderWidth: 1,
      }
    ]
  };
  
  // Dados para gráfico de formas de pagamento
  const paymentMethodData = {
    labels: ['Cartão de crédito', 'Dinheiro', 'Pix', 'Débito', 'Outros'],
    datasets: [
      {
        data: [45, 20, 25, 8, 2],
        backgroundColor: [
          'rgba(59, 130, 246, 0.7)',
          'rgba(16, 185, 129, 0.7)',
          'rgba(245, 158, 11, 0.7)',
          'rgba(139, 92, 246, 0.7)',
          'rgba(107, 114, 128, 0.7)'
        ],
        borderColor: [
          'rgb(59, 130, 246)',
          'rgb(16, 185, 129)',
          'rgb(245, 158, 11)',
          'rgb(139, 92, 246)',
          'rgb(107, 114, 128)'
        ],
        borderWidth: 1,
      }
    ]
  };

  // Opções para gráficos
  const lineOptions = {
    responsive: true,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += 'R$ ' + context.parsed.y.toLocaleString('pt-BR', { minimumFractionDigits: 2 });
            }
            return label;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(value) {
            return 'R$ ' + value.toLocaleString('pt-BR');
          }
        }
      }
    }
  };
  
  const barOptions = {
    responsive: true,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += 'R$ ' + context.parsed.y.toLocaleString('pt-BR', { minimumFractionDigits: 2 });
            }
            return label;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(value) {
            return 'R$ ' + value.toLocaleString('pt-BR');
          }
        }
      }
    }
  };
  
  const doughnutOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          font: {
            size: isMobile ? 10 : 12
          }
        }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const label = context.label || '';
            const value = context.raw || 0;
            return `${label}: ${value}%`;
          }
        }
      }
    },
    cutout: '60%',
  };

  // Estatísticas financeiras para cards
  const financialStats = {
    totalRevenue: metrics?.totalRevenue || 12500,
    revenueChange: metrics?.revenueChange || 15,
    avgTicket: (metrics?.totalRevenue || 12500) / (metrics?.totalAppointments || 83),
    avgTicketChange: metrics?.avgTicketChange || 8,
    mostProfitableService: 'Coloração',
    mostProfitableServiceRevenue: 3125, // 25% do faturamento total
    revenuePerDay: (metrics?.totalRevenue || 12500) / 30,
    projectedRevenue: (metrics?.totalRevenue || 12500) * 1.1, // estimativa de 10% de aumento
    serviceCount: metrics?.servicePerformance?.length || 6,
    paymentMethods: 5
  };

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-bold text-gray-800">Desempenho Financeiro</h2>
      
      {/* Cards principais */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
        >
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-500">Faturamento</h3>
            <div className="bg-blue-100 p-2 rounded-lg">
              <CurrencyDollarIcon className="h-5 w-5 text-blue-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-800">
            R$ {financialStats.totalRevenue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
          </p>
          <div className="mt-2 flex items-center text-xs">
            <ArrowTrendingUpIcon className={`h-3 w-3 ${financialStats.revenueChange > 0 ? 'text-green-500' : 'text-red-500'} mr-1`} />
            <span className={`${financialStats.revenueChange > 0 ? 'text-green-500' : 'text-red-500'} font-medium`}>
              {financialStats.revenueChange}%
            </span>
            <span className="text-gray-400 ml-1">vs. período anterior</span>
          </div>
        </motion.div>
        
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
        >
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-500">Ticket Médio</h3>
            <div className="bg-purple-100 p-2 rounded-lg">
              <BanknotesIcon className="h-5 w-5 text-purple-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-800">
            R$ {financialStats.avgTicket.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
          </p>
          <div className="mt-2 flex items-center text-xs">
            <ArrowTrendingUpIcon className={`h-3 w-3 ${financialStats.avgTicketChange > 0 ? 'text-green-500' : 'text-red-500'} mr-1`} />
            <span className={`${financialStats.avgTicketChange > 0 ? 'text-green-500' : 'text-red-500'} font-medium`}>
              {financialStats.avgTicketChange}%
            </span>
            <span className="text-gray-400 ml-1">vs. período anterior</span>
          </div>
        </motion.div>
        
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
        >
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-500">Serviço mais rentável</h3>
            <div className="bg-pink-100 p-2 rounded-lg">
              <TagIcon className="h-5 w-5 text-pink-600" />
            </div>
          </div>
          <p className="text-lg font-bold text-gray-800 truncate">{financialStats.mostProfitableService}</p>
          <p className="text-sm text-gray-600">
            R$ {financialStats.mostProfitableServiceRevenue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
          </p>
          <div className="mt-1 flex items-center text-xs">
            <span className="text-gray-400">25% do faturamento total</span>
          </div>
        </motion.div>
        
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
        >
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-500">Projeção Mensal</h3>
            <div className="bg-green-100 p-2 rounded-lg">
              <ChartBarIcon className="h-5 w-5 text-green-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-800">
            R$ {financialStats.projectedRevenue.toLocaleString('pt-BR', { minimumFractionDigits: 0 })}
          </p>
          <div className="mt-2 flex items-center text-xs">
            <ArrowTrendingUpIcon className="h-3 w-3 text-green-500 mr-1" />
            <span className="text-green-500 font-medium">+10%</span>
            <span className="text-gray-400 ml-1">estimativa</span>
          </div>
        </motion.div>
      </div>
      
      {/* Gráfico de faturamento por período */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
      >
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Faturamento por Período</h3>
          <div className="flex space-x-1 bg-gray-100 rounded-lg p-1">
            <button 
              onClick={() => setSelectedPeriod('month')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md ${
                selectedPeriod === 'month' 
                  ? 'bg-white text-blue-600 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Mês
            </button>
            <button 
              onClick={() => setSelectedPeriod('quarter')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md ${
                selectedPeriod === 'quarter' 
                  ? 'bg-white text-blue-600 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Trimestre
            </button>
            <button 
              onClick={() => setSelectedPeriod('year')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md ${
                selectedPeriod === 'year' 
                  ? 'bg-white text-blue-600 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Ano
            </button>
          </div>
        </div>
        
        <div className="h-72">
          <Line data={revenueData[selectedPeriod]} options={lineOptions} />
        </div>
        
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <p className="text-xs text-gray-500">Média Diária</p>
            <p className="text-sm font-bold">
              R$ {financialStats.revenuePerDay.toLocaleString('pt-BR', { minimumFractionDigits: 0 })}
            </p>
          </div>
          <div className="text-center">
            <p className="text-xs text-gray-500">Maior Dia</p>
            <p className="text-sm font-bold">20/05 (R$ 620)</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-gray-500">Menor Dia</p>
            <p className="text-sm font-bold">01/05 (R$ 320)</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-gray-500">Tendência</p>
            <p className="text-sm font-bold text-green-500">+15% ao mês</p>
          </div>
        </div>
      </motion.div>
      
      {/* Gráficos de análise */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
        >
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Ticket Médio</h3>
            <button 
              onClick={() => setShowDetails(showDetails === 'ticket' ? null : 'ticket')}
              className="text-blue-600 text-sm font-medium hover:text-blue-700"
            >
              {showDetails === 'ticket' ? 'Ocultar detalhes' : 'Ver detalhes'}
            </button>
          </div>
          
          <div className="h-48">
            <Bar data={avgTicketData} options={barOptions} />
          </div>
          
          {showDetails === 'ticket' && (
            <motion.div 
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="mt-4 border-t border-gray-100 pt-4"
            >
              <h4 className="font-medium text-sm mb-2">Análise de Ticket</h4>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <p className="text-sm text-gray-600">Ticket mais alto</p>
                  <p className="text-sm font-medium">Cliente premium (R$ 280)</p>
                </div>
                <div className="flex justify-between items-center">
                  <p className="text-sm text-gray-600">Ticket mais comum</p>
                  <p className="text-sm font-medium">R$ 120 - R$ 150</p>
                </div>
                <div className="flex justify-between items-center">
                  <p className="text-sm text-gray-600">Serviços por ticket</p>
                  <p className="text-sm font-medium">1,8 serviços/cliente</p>
                </div>
                <div className="p-3 bg-blue-50 rounded-lg mt-2">
                  <p className="text-xs text-blue-800">
                    <span className="font-medium">Dica:</span> Ofereça pacotes combinados para aumentar o ticket médio em 25%.
                  </p>
                </div>
              </div>
            </motion.div>
          )}
        </motion.div>
        
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
        >
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Faturamento por Serviço</h3>
            <button 
              onClick={() => setShowDetails(showDetails === 'service' ? null : 'service')}
              className="text-blue-600 text-sm font-medium hover:text-blue-700"
            >
              {showDetails === 'service' ? 'Ocultar detalhes' : 'Ver detalhes'}
            </button>
          </div>
          
          <div className="h-48 flex items-center justify-center">
            <Doughnut data={serviceRevenueData} options={doughnutOptions} />
          </div>
          
          {showDetails === 'service' && (
            <motion.div 
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="mt-4 border-t border-gray-100 pt-4"
            >
              <h4 className="font-medium text-sm mb-2">Análise de Serviços</h4>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <p className="text-sm text-gray-600">Mais lucrativo</p>
                  <p className="text-sm font-medium">Coloração (35% margem)</p>
                </div>
                <div className="flex justify-between items-center">
                  <p className="text-sm text-gray-600">Maior crescimento</p>
                  <p className="text-sm font-medium">Tratamentos (+22%)</p>
                </div>
                <div className="flex justify-between items-center">
                  <p className="text-sm text-gray-600">Maior queda</p>
                  <p className="text-sm font-medium">Pedicure (-5%)</p>
                </div>
                <div className="p-3 bg-purple-50 rounded-lg mt-2">
                  <p className="text-xs text-purple-800">
                    <span className="font-medium">Dica:</span> Priorize tratamentos capilares para aumentar a receita recorrente.
                  </p>
                </div>
              </div>
            </motion.div>
          )}
        </motion.div>
      </div>
      
      {/* Formas de pagamento */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
        className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
      >
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Formas de Pagamento</h3>
          <div className="bg-blue-100 p-1.5 rounded-md">
            <CreditCardIcon className="h-5 w-5 text-blue-600" />
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="h-56 flex items-center justify-center">
            <Doughnut data={paymentMethodData} options={doughnutOptions} />
          </div>
          
          <div className="flex flex-col justify-center space-y-3">
            <div className="p-3 border border-gray-100 rounded-lg bg-gray-50">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm font-medium">Cartão de crédito</p>
                  <p className="text-xs text-gray-500">Parcelamento médio: 2x</p>
                </div>
                <p className="text-lg font-bold text-blue-600">45%</p>
              </div>
            </div>
            
            <div className="p-3 border border-gray-100 rounded-lg">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm font-medium">PIX</p>
                  <p className="text-xs text-gray-500">+35% vs. período anterior</p>
                </div>
                <p className="text-lg font-bold text-amber-600">25%</p>
              </div>
            </div>
            
            <div className="p-3 border border-gray-100 rounded-lg">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm font-medium">Dinheiro</p>
                  <p className="text-xs text-gray-500">-10% vs. período anterior</p>
                </div>
                <p className="text-lg font-bold text-green-600">20%</p>
              </div>
            </div>
            
            <div className="p-3 bg-blue-50 rounded-lg">
              <p className="text-sm font-medium text-blue-800">Dica para otimização</p>
              <p className="text-xs text-blue-700 mt-1">
                Ofereça 5% de desconto para pagamentos via PIX para reduzir taxas de cartão.
              </p>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default FinancialSection; 